import { bezSierotek } from '@insolitum/ui';
import { AlertCircle, KeyRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { POLE_WEJSCIA, UkladWejscia } from '../../components/marka/UkladWejscia';
import { pobierzCzynniki, potwierdzKod, wymagaKodu } from '../../lib/dwuskladnikowe';
import { useAuth } from '../../providers/AuthProvider';

/**
 * Ekran kodu 2FA po haśle.
 * =======================
 *
 * Hasło daje sesję `aal1`; konto ze zweryfikowanym czynnikiem ma `nextLevel`
 * `aal2`, więc `ProtectedRoute` odsyła tu każdą trasę chronioną, dopóki kod
 * nie podniesie sesji. Kod jest weryfikowany przez `challengeAndVerify` —
 * auth-js zapisuje nowy token i emituje `MFA_CHALLENGE_VERIFIED`, na które
 * AuthProvider odświeża `stanMfa`; wtedy wracamy tam, skąd użytkownik przyszedł.
 *
 * „Zaloguj się na inne konto" kończy sesję lokalnie — kto nie ma telefonu,
 * nie ma jak wejść, ale też nie zostaje uwięziony na tym ekranie.
 */
export function DwuskladnikowePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, stanMfa, signOut } = useAuth();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [kod, setKod] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [zajety, setZajety] = useState(false);

  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/';

  // Bez sesji → logowanie; sesja bez wymogu kodu (brak czynnika albo kod już
  // podany) → tam, skąd przyszła. Ten ekran istnieje tylko dla jednego stanu.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (!wymagaKodu(stanMfa)) navigate(from, { replace: true });
  }, [isAuthenticated, isLoading, stanMfa, from, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let aktywny = true;
    void pobierzCzynniki()
      .then((czynniki) => {
        if (!aktywny) return;
        const zweryfikowany = czynniki.find((c) => c.status === 'verified');
        setFactorId(zweryfikowany?.id ?? null);
      })
      .catch((err: Error) => {
        if (aktywny) setError(err.message);
      });
    return () => {
      aktywny = false;
    };
  }, [isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setError(null);
    setZajety(true);
    try {
      await potwierdzKod(factorId, kod);
      // Nawigację robi efekt wyżej po odświeżeniu `stanMfa` (MFA_CHALLENGE_VERIFIED).
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setZajety(false);
    }
  };

  const inneKonto = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <UkladWejscia>
      <div className="authcard">
        <h1 className="authcard__h flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-aurora-brand" />
          {bezSierotek(t('dwuskladnikowe.ekranTytul', 'Kod z aplikacji'))}
        </h1>
        <p className="authcard__s">
          {bezSierotek(
            t(
              'dwuskladnikowe.ekranOpis',
              'To konto ma włączone uwierzytelnianie dwuskładnikowe. Wpisz kod z aplikacji uwierzytelniającej.'
            )
          )}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div
              role="alert"
              className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-ids-md text-red-400 [[data-mode=light]_&]:text-red-800 text-sm"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {bezSierotek(error)}
            </div>
          )}

          <div className="field">
            <label htmlFor="kod-2fa" className="field__label">
              {bezSierotek(t('dwuskladnikowe.kod', 'Kod z aplikacji'))}
            </label>
            <input
              id="kod-2fa"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 ]*"
              maxLength={7}
              value={kod}
              onChange={(e) => setKod(e.target.value)}
              required
              autoFocus
              className={`input text-center tracking-[0.4em] text-lg ${POLE_WEJSCIA}`}
              placeholder="123 456"
            />
            <p className="text-xs text-aurora-subtle mt-1">
              {t('dwuskladnikowe.kodPodpowiedz', 'Sześć cyfr, kod zmienia się co 30 sekund.')}
            </p>
          </div>

          <button
            type="submit"
            disabled={zajety || !factorId}
            className="btn btn--primary btn--block"
          >
            {zajety ? <span className="spinner" /> : t('dwuskladnikowe.ekranZatwierdz', 'Zaloguj')}
          </button>
        </form>

        <div className="mt-6 h-px bg-aurora-border" aria-hidden="true" />
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => void inneKonto()}
            className="text-[13px] font-semibold text-aurora-brand hover:text-aurora-brand-hover transition-colors"
          >
            {t('dwuskladnikowe.ekranInneKonto', 'Zaloguj się na inne konto')}
          </button>
        </div>
      </div>
    </UkladWejscia>
  );
}
